import type { SpendApprovalDetails, SpendApprovalFact } from '@dreamai/types';
import type { HotelRateQuote } from '../chat/sidebar/useHotelRateQuote';
import { ukDate, timeOf } from './cards';
import { headline } from './Details';
import type { StayOption, TransportOption, TravelOption } from './data';

interface Selection {
  offerId?: string;
  farePosition?: number;
  quote?: HotelRateQuote;
  separateTickets?: boolean;
}

function readableList(values: string[]): string {
  if (values.length < 2) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

function carriersForLeg(leg: TransportOption['legs'][number]): string[] {
  return Array.from(
    new Set(leg.segments.map((segment) => segment.carrierName).filter(Boolean)),
  );
}

function sameCarriers(first: string[], second: string[]): boolean {
  return (
    first.length === second.length &&
    first.every((carrier) => second.includes(carrier))
  );
}

function flightSubline(option: TransportOption): string {
  const outbound = option.legs[0];
  const outboundCarriers = carriersForLeg(outbound);
  const outboundTime = `${ukDate(outbound.dep)} at ${timeOf(outbound.dep)}`;
  if (option.legs.length < 2) {
    const carriers = readableList(outboundCarriers);
    return carriers ? `${outboundTime}, ${carriers}` : outboundTime;
  }

  const inbound = option.legs[1];
  const inboundCarriers = carriersForLeg(inbound);
  const inboundTime = `${ukDate(inbound.dep)} at ${timeOf(inbound.dep)}`;
  if (sameCarriers(outboundCarriers, inboundCarriers)) {
    const carriers = readableList(outboundCarriers);
    return `Out ${outboundTime}, back ${inboundTime}${carriers ? `. ${carriers}` : ''}`;
  }

  const outboundWithCarrier = outboundCarriers.length
    ? ` with ${readableList(outboundCarriers)}`
    : '';
  const inboundWithCarrier = inboundCarriers.length
    ? ` with ${readableList(inboundCarriers)}`
    : '';
  return `Out ${outboundTime}${outboundWithCarrier}, back ${inboundTime}${inboundWithCarrier}`;
}

function flightDetails(
  option: TransportOption,
  selection: Selection,
): { details: SpendApprovalDetails; price: number; currency: string } {
  const fare =
    option.fares.find((candidate) => candidate.id === selection.offerId) ??
    option.fares[selection.farePosition ?? -1];
  const segments = option.legs.flatMap((leg) =>
    leg.segments.map((segment) => segment.number),
  );
  const facts: SpendApprovalFact[] = [
    ...(segments.length > 0
      ? [{ label: 'Flights', value: readableList(segments) }]
      : []),
    ...(fare
      ? [
          {
            label: 'Fare',
            value: [fare.name, fare.nameDetail].filter(Boolean).join(' '),
          },
        ]
      : []),
    ...(fare?.bags !== null && fare?.bags !== undefined
      ? [{ label: 'Bags', value: fare.bags }]
      : []),
  ];
  const terms = fare?.conditions.trim() ? [fare.conditions] : undefined;

  return {
    details: {
      headline: headline(option),
      subline: flightSubline(option),
      ...(terms ? { terms } : {}),
      ...(facts.length > 0 ? { facts } : {}),
      ...(selection.separateTickets
        ? {
            notice:
              'These flights are booked separately. If one is disrupted, the other airline is not required to rebook you.',
          }
        : {}),
    },
    price: fare?.total ?? option.price,
    currency: fare?.currency ?? option.currency,
  };
}

function hotelDateRange(checkIn: string, checkOut: string): string {
  const start = new Date(`${checkIn.slice(0, 10)}T12:00:00Z`);
  const end = new Date(`${checkOut.slice(0, 10)}T12:00:00Z`);
  const startLabel = ukDate(checkIn);
  const endLabel = ukDate(checkOut);
  const sameMonth =
    start.getUTCFullYear() === end.getUTCFullYear() &&
    start.getUTCMonth() === end.getUTCMonth();
  const shortStart = sameMonth
    ? startLabel.split(' ').slice(0, 2).join(' ')
    : startLabel;
  return `${shortStart} – ${endLabel}`;
}

function hotelDetails(
  option: StayOption,
  selection: Selection,
): { details: SpendApprovalDetails; price: number; currency: string } {
  const rate =
    option.rates.find((candidate) => candidate.id === selection.offerId) ??
    option.rates[selection.farePosition ?? -1];
  const nights = `${option.nights} ${option.nights === 1 ? 'night' : 'nights'}`;
  const guests = `${option.guests} ${option.guests === 1 ? 'guest' : 'guests'}`;
  const quote = selection.quote;
  const refundable = quote ? quote.refundable : rate?.refundable;
  const freeCancellationUntil = quote
    ? quote.freeCancellationUntil
    : rate?.freeUntil;
  const terms =
    refundable == null
      ? undefined
      : [
          refundable
            ? freeCancellationUntil
              ? `Free cancellation until ${ukDate(freeCancellationUntil)}`
              : 'Free cancellation'
            : 'Non-refundable',
        ];
  const facts: SpendApprovalFact[] = rate
    ? [
        ...(rate.room ? [{ label: 'Room', value: rate.room }] : []),
        ...(rate.board ? [{ label: 'Board', value: rate.board }] : []),
      ]
    : [];

  return {
    details: {
      headline: option.title,
      subline: `${hotelDateRange(option.checkIn, option.checkOut)}, ${nights}, ${guests}`,
      ...(terms ? { terms } : {}),
      ...(facts.length > 0 ? { facts } : {}),
    },
    price: quote?.total ?? rate?.total ?? option.price,
    currency: quote?.currency ?? rate?.currency ?? option.currency,
  };
}

export function travelApprovalDetails(
  option: TravelOption,
  selection: Selection,
): { details: SpendApprovalDetails; price: number; currency: string } {
  if (option.kind === 'hotel') return hotelDetails(option, selection);
  if (option.kind === 'train') {
    return {
      details: { headline: option.title },
      price: option.price,
      currency: option.currency,
    };
  }
  return flightDetails(option, selection);
}
